"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const differenceInMinutes_1 = __importDefault(require("date-fns/differenceInMinutes"));
const find_1 = __importDefault(require("lodash/find"));
const get_1 = __importDefault(require("lodash/get"));
const keyBy_1 = __importDefault(require("lodash/keyBy"));
const map_1 = __importDefault(require("lodash/map"));
const size_1 = __importDefault(require("lodash/size"));
const mobx_1 = require("mobx");
const getFleetPricing_1 = __importDefault(require("../clients/fleetprice/getFleetPricing"));
const API_STATE_1 = __importDefault(require("../constants/app/API_STATE"));
const FLEET_PRICE_API_STATUS_1 = __importDefault(require("../constants/app/FLEET_PRICE_API_STATUS"));
const PROMO_TYPES_1 = __importDefault(require("../constants/app/PROMO_TYPES"));
const fleetPriceFactory_1 = __importDefault(require("../factories/fleetprice/fleetPriceFactory"));
const getPriceNotFound_1 = __importDefault(require("../utils/error/getPriceNotFound"));
const getPriceSoldOut_1 = __importDefault(require("../utils/error/getPriceSoldOut"));
const mapInitialStateToStoreState_1 = __importDefault(require("../utils/store/mapInitialStateToStoreState"));
/**
 * Fleet Price Domain Store
 * Stores fleet pricing list.
 */
class FleetPriceStore {
    constructor(initialState, $env) {
        this.data = [];
        this.searchId = '';
        this.apiState = API_STATE_1.default.INITIAL;
        this.hasPricesLoadedfromAPI = false; // tells us if price api response loaded from cache or fresh call
        this.errorData = {};
        this.expiry = 0; // search expiry in mins
        /**
         * Fetches Fleet Group Pricing data from backend via Long polling
         * @param {string} reqDataStr
         * @param {string} successHandler
         * @param {ErrorHandlerType} errorHandler
         */
        this.fetch = (0, mobx_1.flow)(function* (reqData, successHandler, errorHandler) {
            try {
                this.apiState = API_STATE_1.default.LOADING;
                this.data = [];
                this.searchId = '';
                this.hasPricesLoadedfromAPI = false;
                this.errorData = {};
                // make the api call
                const fleetPriceResponse = yield (0, getFleetPricing_1.default)(reqData);
                const { data, total, status, searchId, expiry } = fleetPriceResponse;
                if (status === FLEET_PRICE_API_STATUS_1.default.POLL_TIMEOUT) {
                    /**
                     * Long poll timed out
                     * The API took long time to respond back
                     */
                    // throw custom error
                    const errObj = (0, getPriceNotFound_1.default)();
                    this.onError(errObj, errorHandler);
                }
                else if (status === FLEET_PRICE_API_STATUS_1.default.COMPLETE && total === 0) {
                    /**
                     * for no data, response comes back with
                     * empty data[], status - complete, total = 0
                     * Sold Out
                     */
                    // throw custom error
                    const errObj = (0, getPriceSoldOut_1.default)();
                    this.onError(errObj, errorHandler);
                }
                else {
                    // http 200 and with data
                    this.onSuccess(data, searchId, true, expiry, successHandler, total);
                }
            }
            catch (err) {
                if (err === null || err === void 0 ? void 0 : err.response) {
                    // all other non http 200 error
                    this.onError(err, errorHandler);
                }
            }
        });
        /**
         * on fleet price success callback
         * @param {FleetPriceType[]} data
         * @param {string} searchId
         * @param {boolean} isFromAPI
         * @param {number} expiry
         * @param {Function} successHandler,
         * @param {number} total
         */
        this.onSuccess = (data, searchId, isFromAPI = false, expiry, successHandler, total) => {
            this.data = data;
            this.searchId = searchId;
            this.hasPricesLoadedfromAPI = isFromAPI;
            this.apiState = API_STATE_1.default.SUCCESS;
            // convert expiry sent by API to mins
            const expiryInMins = (0, differenceInMinutes_1.default)(new Date(expiry * 1000), // to JS timestamp
            Date.now());
            // update observable
            this.expiry = expiryInMins;
            if (successHandler) {
                successHandler(total);
            }
        };
        /**
         * on fleet price error callback
         * @param err
         * @param errorHandler
         */
        this.onError = (err, errorHandler) => {
            this.data = [];
            this.searchId = '';
            this.hasPricesLoadedfromAPI = false;
            this.apiState = API_STATE_1.default.FAILED;
            if (err.response) {
                const errResponse = err.response;
                const { data, status } = errResponse;
                // stores axios error info in observable
                this.errorData = {
                    data,
                    status,
                };
                // call error handler callback if any
                if (errorHandler) {
                    errorHandler(errResponse);
                }
            }
        };
        /**
         * Map initial state to store state
         * @param initialState
         */
        this.updateInitialState = (initialState) => {
            (0, mapInitialStateToStoreState_1.default)(this, initialState);
        };
        this.$env = $env;
        this.updateInitialState(initialState);
    }
    /**
     * Returns true the loading status for price call
     * @returns {Boolean}
     */
    get isLoading() {
        return this.apiState === API_STATE_1.default.LOADING;
    }
    /**
     * Returns true if price API fetch was success
     * @returns {Boolean}
     */
    get isSuccess() {
        return this.apiState === API_STATE_1.default.SUCCESS;
    }
    /**
     * Returns true if price API fetch has failed
     * @returns {Boolean}
     */
    get hasFailed() {
        return this.apiState === API_STATE_1.default.FAILED;
    }
    /**
     * Returns true if price API fetch returned no data
     * @returns {Boolean}
     */
    get hasNoData() {
        return this.isSuccess && this.count === 0;
    }
    /**
     * Returns total fleet price count
     * @returns {Number}
     */
    get count() {
        return (0, size_1.default)(this.fleetPriceList);
    }
    /**
     * Returns current config language
     * @returns {LANG_VALUES}
     */
    get language() {
        return this.$env.config.locale;
    }
    /**
     * Returns formatted fleet price list
     * @return {Array}
     */
    get fleetPriceList() {
        return (0, map_1.default)(this.data, (data) => (0, fleetPriceFactory_1.default)(data, this.language));
    }
    /**
     * Returns an object hashmap of fleet prices by groupId
     * @returns {Object}
     */
    get fleetPriceHashMap() {
        return (0, keyBy_1.default)(this.fleetPriceList, 'groupId');
    }
    /**
     * Returns true if atleast one car group has all discount
     * @returns {boolean}
     */
    get hasAllDiscount() {
        return this.fleetPriceList.some((price) => {
            return price.hasAllDiscount;
        });
    }
    /**
     * Returns true if at least one car group has online discount
     * @returns {boolean}
     */
    get hasOnlineDiscount() {
        return this.fleetPriceList.some((price) => {
            return price.hasOnlineDiscount;
        });
    }
    /**
     * Returns true if atleast one unpromoted car group has all discount
     * @returns {boolean}
     */
    get hasUnPromotedAllDiscount() {
        return this.fleetPriceList.some((price) => {
            return price.hasAllDiscount && !price.promoted;
        });
    }
    /**
     * Returns true if at least one unpromoted car group has online discount
     * @returns {boolean}
     */
    get hasUnPromotedOnlineDiscount() {
        return this.fleetPriceList.some((price) => {
            return price.hasOnlineDiscount && !price.promoted;
        });
    }
    /**
     * Returns the max online or all discount for car groups
     * @returns {number}
     */
    get maximumDiscount() {
        const discountArr = []; // array of numbers
        this.fleetPriceList.forEach((price) => {
            const discountPercent = (0, get_1.default)(price, 'discount.discountPercent', 0);
            // Exclude Trending deals discount
            const isPromoted = (0, get_1.default)(price, 'promoted', false);
            if (!isPromoted) {
                discountArr.push(discountPercent);
            }
        });
        return Math.max(...discountArr);
    }
    /**
     * Returns true if there is an auto applied promo code
     * for atleast one car group
     * @returns {boolean}
     */
    get hasAutoAppliedPromoCode() {
        return (this.isSuccess &&
            (this.hasUnPromotedAllDiscount || this.hasUnPromotedOnlineDiscount));
    }
    /**
     * Returns the auto applied promo discount percentage
     * @returns {string}
     */
    get autoAppliedDiscountPercent() {
        if (this.hasAutoAppliedPromoCode) {
            return `${this.maximumDiscount}`;
        }
        return '';
    }
    /**
     * Returns whether the max discount belongs to
     * all or online
     * @returns {string}
     */
    get maxDiscAllOrOnline() {
        let returnStr = '';
        // find the max discount fleet price object
        const maxDiscountObj = (0, find_1.default)(this.fleetPriceList, (price) => {
            return (this.maximumDiscount === (0, get_1.default)(price, 'discount.discountPercent', 0));
        });
        if ((0, get_1.default)(maxDiscountObj, 'hasAllDiscount', false)) {
            returnStr = PROMO_TYPES_1.default.ALL;
        }
        // online takes priority
        if ((0, get_1.default)(maxDiscountObj, 'hasOnlineDiscount', false)) {
            returnStr = PROMO_TYPES_1.default.ONLINE;
        }
        return returnStr;
    }
}
__decorate([
    mobx_1.observable
], FleetPriceStore.prototype, "data", void 0);
__decorate([
    mobx_1.observable
], FleetPriceStore.prototype, "searchId", void 0);
__decorate([
    mobx_1.observable
], FleetPriceStore.prototype, "apiState", void 0);
__decorate([
    mobx_1.observable
], FleetPriceStore.prototype, "hasPricesLoadedfromAPI", void 0);
__decorate([
    mobx_1.observable
], FleetPriceStore.prototype, "errorData", void 0);
__decorate([
    mobx_1.observable
], FleetPriceStore.prototype, "expiry", void 0);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "isLoading", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "isSuccess", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "hasFailed", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "hasNoData", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "count", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "language", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "fleetPriceList", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "fleetPriceHashMap", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "hasAllDiscount", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "hasOnlineDiscount", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "hasUnPromotedAllDiscount", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "hasUnPromotedOnlineDiscount", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "maximumDiscount", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "hasAutoAppliedPromoCode", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "autoAppliedDiscountPercent", null);
__decorate([
    mobx_1.computed
], FleetPriceStore.prototype, "maxDiscAllOrOnline", null);
__decorate([
    mobx_1.action.bound
], FleetPriceStore.prototype, "onSuccess", void 0);
__decorate([
    mobx_1.action.bound
], FleetPriceStore.prototype, "onError", void 0);
__decorate([
    mobx_1.action.bound
], FleetPriceStore.prototype, "updateInitialState", void 0);
exports.default = FleetPriceStore;
